import { useEffect, useState } from "react";
import { CheckCircleIcon, UserIcon, EyeIcon, EyeSlashIcon, XCircleIcon, MagnifyingGlassIcon, CalendarIcon } from "@heroicons/react/24/outline";
import { useVerification, VerificationStatus } from "../../contexts/VerificationContext";
import Loading from "../Loading";
import { useOrganizationContext } from "src/contexts/OrganizationContext";
import Modal from "../modals/Modal";
import DirectoryProvider from "src/contexts/DirectoryContext";
import { DirectoryBody } from "../directory/Directory";
import { getPublicAppConfig } from "src/utils/runtimeConfig";

interface PatientInfoProps {
  onPatientLookupClick?: () => void;
}

const PatientInfo = ({ onPatientLookupClick }: PatientInfoProps) => {
  const { patientData, loadingPatientData, patientId } = useOrganizationContext();
  const { verificationStatus, verificationLogId, handleVerify, isVerified } = useVerification();
  const { demoEpicPatientId, demoCernerPatientId } = getPublicAppConfig();
  const isDemoPatient = patientId === demoEpicPatientId || patientId === demoCernerPatientId;
  const [ showScheduleModal, setShowScheduleModal ] = useState(false);
  const [ showDetails, setShowDetails ] = useState(true);

  useEffect(() => {
    if (isVerified) {
      setShowDetails(false);
    }
  }, [ isVerified ]);

  const extractZipCode = (address: string | undefined): string | undefined => {
    if (!address) return undefined;
    const zipMatch = address.match(/\b(\d{5})(?:-(\d{4}))?\b/);
    return zipMatch ? zipMatch[1] : undefined;
  };

  const ScheduleButton = () => (
    <button
      className="inline-flex items-center gap-2 ml-2 px-4 py-2 border border-brand-600 bg-white hover:bg-slate-50 text-brand-600 text-sm font-medium rounded-lg transition-colors shadow-sm"
      onClick={() => {
        if (isVerified) {
          setShowScheduleModal(true);
        }
      }}
      disabled={!isVerified}
    >
      <CalendarIcon className="w-4 h-4" />
      Schedule
    </button>
  );

  const verifyBtnColor = () => {
    if (!verificationLogId) return 'bg-gray-600 text-white disabled cursor-not-allowed';

    if (verificationStatus === VerificationStatus.NOT_VERIFIED) {
      return 'bg-brand-600 hover:bg-brand-700 text-white';
    } else if (verificationStatus === VerificationStatus.VERIFIED) {
      return 'bg-brand-100 text-brand-800';
    } else {
      return 'bg-red-600 text-white cursor-not-allowed';
    }
  }

  if (loadingPatientData) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8">
        <div className="flex items-center justify-center">
          <Loading message="Loading Patient Info..." borderColor="border-rose-500" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-y-4">
            <div className="flex items-start gap-4">
              {isDemoPatient ? (
                <img src="/example_dude.jpg" alt="Patient" className="h-[60px] w-[60px] rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="bg-brand-100 p-3 rounded-full flex-shrink-0">
                  <UserIcon className="h-9 w-9 text-brand-600" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <h1 className="text-lg font-bold text-gray-900">{patientData?.name || 'Unknown'}</h1>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1 text-sm pb-1">
                  <span className="text-gray-500 font-medium uppercase tracking-wider">{patientData?.primaryIdentifier.name}:</span>
                  <span className="font-semibold text-gray-700">{patientData?.primaryIdentifier.value}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 pt-1 text-xs">
                  <div className="flex items-center gap-1.5">
                    {patientData?.age && patientData.age > -1 ? `${patientData.age} year old` : ''}
                  </div>
                  <div className="hidden sm:block w-px h-4 bg-gray-200"></div>
                  <div className="flex items-center gap-1.5">
                    {patientData?.gender}
                  </div>
                  <div className="hidden sm:block w-px h-4 bg-gray-200"></div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-500 font-medium uppercase tracking-wider">DOB:</span>
                    <span className="font-semibold text-gray-700">{patientData?.dateOfBirth || 'Unknown'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0 w-full md:w-auto pl-[72px] md:pl-0">
              {isVerified && <ScheduleButton />}

              {onPatientLookupClick && (
                <button
                  type="button"
                  onClick={onPatientLookupClick}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-brand-600 bg-white hover:bg-slate-50 text-brand-600 transition-colors"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                  Patient lookup
                </button>
              )}

              {!isVerified && (
                <div className="hidden md:block">
                  <button
                    onClick={handleVerify}
                    disabled={verificationStatus === VerificationStatus.FAILED || !verificationLogId}
                    className={`${verifyBtnColor()} inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm`}
                  >
                    {verificationStatus === VerificationStatus.FAILED ? (
                      <XCircleIcon className="w-4 h-4" />
                    ) : (
                      <CheckCircleIcon className="w-4 h-4" />
                    )}
                    {verificationStatus}
                  </button>
                </div>
              )}

              {isVerified && (
                <div className="hidden md:block relative group">
                  <button
                    onClick={() => setShowDetails((isShown) => !isShown)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all duration-200"
                  >
                    {showDetails ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {showDetails ? 'Hide Information' : 'Show Information'}
                  </span>
                </div>
              )}
            </div>

            {!isVerified && (
              <div className="block md:hidden pl-[72px] space-y-4">
                <div>
                  <button
                    onClick={handleVerify}
                    disabled={verificationStatus === VerificationStatus.FAILED || !verificationLogId}
                    className={`${verifyBtnColor()} inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm`}
                  >
                    {verificationStatus === VerificationStatus.FAILED ? (
                      <XCircleIcon className="w-4 h-4" />
                    ) : (
                      <CheckCircleIcon className="w-4 h-4" />
                    )}
                    {verificationStatus}
                  </button>
                </div>
                <div>
                  <button
                    onClick={() => setShowDetails((isShown) => !isShown)}
                    className="bg-gray-100 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all duration-200"
                  >
                    {
                      showDetails ?
                      <span className="flex items-center gap-2">Hide Information <EyeSlashIcon className="w-5 h-5" /></span> :
                      <span className="flex items-center gap-2">Show Information <EyeIcon className="w-5 h-5" /></span>
                    }
                  </button>
                </div>
              </div>

            )}
          </div>
        </div>

        <div className={`w-fit px-6 transition-all duration-300 ease-in-out overflow-hidden ${showDetails ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
            <div className="py-3">
              <p className="text-xs text-brand-600 font-semibold uppercase tracking-wider mb-1">Contact Number</p>
              <p className="text-sm text-gray-900">{patientData?.phone || 'Not provided'}</p>
            </div>

            <div className="py-3">
              <p className="text-xs text-brand-600 font-semibold uppercase tracking-wider mb-1">Email</p>
              <p className="text-sm text-gray-900">{patientData?.email || 'Not provided'}</p>
            </div>

            <div className="py-3 lg:col-span-2">
              <p className="text-xs text-brand-600 font-semibold uppercase tracking-wider mb-1">Address</p>
              <p className="text-sm text-gray-900">{patientData?.address || 'Not provided'}</p>
            </div>

            <div className="pt-3 pb-5 lg:col-span-2">
              <p className="text-xs text-brand-600 font-semibold uppercase tracking-wider mb-1">Primary Physician</p>
              <p className="text-sm text-gray-900">{patientData?.primaryPhysician || 'Not assigned'}</p>
            </div>
            <div className="pt-3 pb-5">
              <p className="text-xs text-brand-600 font-semibold uppercase tracking-wider mb-1">Emergency Contact</p>
              <p className="text-sm text-gray-900">{patientData?.emergencyContact || 'Not provided'}</p>
            </div>
          </div>
        </div>
      </div>

      {showScheduleModal &&
        <Modal title="Schedule Appointment" onClose={() => setShowScheduleModal(false)}>
          <DirectoryProvider>
            <DirectoryBody inModal={true} initialZipCode={extractZipCode(patientData?.address)} />
          </DirectoryProvider>
        </Modal>
      }
    </>
  );
};

export default PatientInfo;
